import {
    AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, input
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import {
    MatFormField, MatLabel, MatOption, MatSelect, MatSelectTrigger
} from "@angular/material/select";
import { MatError } from "@angular/material/form-field";
import { LIST_STATE_SERVICE } from "../../constants/tokens";
import { IListStateService } from "../../interfaces/list-state-service";
import { IListSortOption } from "../../interfaces/list-sort-option";
import { DisabledDirective } from "../../../application/directives/disabled/disabled.directive";
import { ProcessingDirective } from "../../../application/directives/processing/processing.directive";
import { AsyncPipe } from "@angular/common";
import { EnhancedControlDirective } from "../../../forms/directives/form-control/enhanced-control.directive";
import { ErrorMessageComponent } from "../../../components/error-message/error-message.component";

/**
 * A standalone component that provides a Material select dropdown for sorting table data.
 *
 * Features:
 * - Integrates with Material Design components
 * - Displays available sort options from the list state service
 * - Automatically updates sort state when user selects a new option
 * - Hides itself when only one sort option is available
 * - Supports processing state and disabled state through ProcessingDirective
 * - Optionally prepends a reset-to-default option (opt-in via {@link defaultOptionLabel})
 *   that clears the sort back to `undefined`. Intended for lists that can start unsorted
 *   (e.g. one initialized with `defaultSortOption: undefined`), so the user can return to
 *   that unsorted state after picking a sort.
 *
 * @example
 * ```html
 * <mgd-sort-select></mgd-sort-select>
 * ```
 *
 * @example
 * ```html
 * <!-- Adds a "Default" option that resets the sort back to unsorted -->
 * <mgd-sort-select [defaultOptionLabel]="'Default'"></mgd-sort-select>
 * ```
 */
@Component({
    selector: "mgd-sort-select",
    imports: [
        MatFormField,
        MatSelect,
        MatSelectTrigger,
        MatOption,
        MatLabel,
        MatError,
        AsyncPipe,
        FormsModule,
        EnhancedControlDirective,
        ErrorMessageComponent
    ],
    templateUrl: "./sort-select.component.html",
    styleUrl: "./sort-select.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    hostDirectives: [
        {
            directive: DisabledDirective,
            inputs: ["disabled"]
        },
        {
            directive: ProcessingDirective,
            inputs: ["disableWhileProcessing"]
        }
    ]
})
export class SortSelectComponent implements AfterViewInit {

    /** Directive that handles the processing and disabled states of the component */
    readonly processing = inject(ProcessingDirective, { self: true });

    /** Service that manages the list state including sort options and current sort */
    readonly stateService = inject(LIST_STATE_SERVICE) as IListStateService;

    /**
     * Opt-in label for a reset-to-default option prepended to the dropdown. When set, an
     * extra `mat-option` mapped to `undefined` is rendered above the state service's sort
     * options; selecting it clears the sort back to unsorted. Omit (leave `undefined`) to
     * preserve the component's default behavior of only listing `sortOptions`.
     */
    readonly defaultOptionLabel = input<string>(undefined);

    /** Reference to Angular's change detector for manual change detection triggering */
    private readonly _cdRef = inject(ChangeDetectorRef);

    /**
     * Gets the available sort options from the state service.
     * Returns an empty array if no options are available.
     */
    get sortOptions(): Array<IListSortOption> {
        return this.stateService.sortOptions || [];
    }

    /**
     * Whether the collapsed select trigger should override its display text to read "Default",
     * given the sort option currently bound to the `mat-select` (passed in from the template's
     * own `stateService.sort$ | async`, rather than read from a separate synchronous getter, so
     * this stays in lockstep with the `[ngModel]` binding under the same `OnPush` change-detection
     * triggers instead of drifting out of sync with it).
     *
     * True only when {@link defaultOptionLabel} is NOT set (that opt-in feature has its own
     * distinct "Default" reset-to-unsorted option and must not be conflated with this one) and
     * `currentSort` is the list's configured {@link IListStateService.defaultSortOption}. This
     * only affects the closed trigger — the open dropdown's `mat-option`s always show their real
     * labels, since `<mat-select-trigger>` is only rendered in the template when this is true.
     */
    isDefaultSort(currentSort: IListSortOption): boolean {
        return !this.defaultOptionLabel() && !!currentSort && currentSort === this.stateService.defaultSortOption;
    }

    /**
     * Determines if the sort select should be displayed.
     * Returns true only if there is more than one selectable option, counting the
     * reset-to-default option ({@link defaultOptionLabel}) when it is set.
     */
    get showSelect(): boolean {
        const optionCount = this.sortOptions.length + (this.defaultOptionLabel() ? 1 : 0);
        return optionCount > 1;
    }

    /**
     * Updates the sort state when user selects a new sort option.
     * Triggers change detection to ensure the view updates.
     * @param sortOption The newly selected sort option, or `undefined` when the
     * reset-to-default option was chosen
     */
    onSortValueChange(sortOption?: IListSortOption): void {
        this.stateService.updateSort(sortOption);
        this._cdRef.markForCheck();
    }

    /**
     * `mat-select`'s initial selection is resolved against its projected `mat-option`s from
     * `ngAfterContentInit`, deferred by Material internally via a microtask so it can run once
     * those options exist (avoiding an "Expression has changed" error). That deferred pass ends
     * with only `markForCheck()`, which — on this `OnPush` component — flags the view as dirty
     * but does not itself schedule a render. When the default sort is already applied before this
     * component is created (the normal, non-`disableDefaultSort` case), nothing else is pending to
     * trigger that render, so the trigger stays blank until an unrelated change detection run
     * happens to sweep through it (e.g. the user opening the panel). Scheduling a microtask here,
     * after Material's own, forces the follow-up render so the default option's label appears
     * immediately instead of waiting on an unrelated event.
     */
    ngAfterViewInit(): void {
        void Promise.resolve().then(() => this._cdRef.detectChanges());
    }
}
